import React from "react";
import { useNavigate } from "react-router-dom";

import type { Program } from "../../shared/programMocks";
import { agentsForProgram } from "../agentsMocks";
import { holdersForProgram } from "../holdersMocks";
import {
    agentToParticipant,
    holderToParticipant,
    type Participant
} from "../ParticipantRow";

import { QualifiedUsers } from "./QualifiedUsers";

interface QualifiedUsersContainerProps {
    program: Program;
}

/**
 * QualifiedUsersContainer — the **Container**. Owns the data seam: selects the
 * roster (`program.target === "holders"` → holders, else agents), maps each
 * entity to a `Participant` via the `agentToParticipant` / `holderToParticipant`
 * adapters (baking in the per-row `/holders?id=…` / `/agents?id=…`
 * navigation), and renders the props-only `QualifiedUsers` View with the
 * resolved list. Imports the View; the View imports no data (firewall).
 *
 * This is the primary entry `ProgramDetail` imports (same `program` prop).
 */
export function QualifiedUsersContainer({
    program
}: QualifiedUsersContainerProps): React.ReactElement {
    const navigate = useNavigate();
    const isHolders = program.target === "holders";

    const participants: Participant[] = isHolders
        ? holdersForProgram(program.id).map((holder) =>
              holderToParticipant(holder, () =>
                  navigate(`/holders?id=${holder.id}`)
              )
          )
        : agentsForProgram(program.id).map((agent) =>
              agentToParticipant(agent, () =>
                  navigate(`/agents?id=${agent.id}`)
              )
          );

    return <QualifiedUsers participants={participants} />;
}
